import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getRowLinkProps, withRowLink } from '../../utils/rowLink';
import { useQuery } from '@apollo/client';
import { getUser, getUserCreditHistory, dashboardUserVideos, dashboardUserImages, getUserNotifications } from '../../graphql/queries';
import { Card, Descriptions, Tag, Spin, Alert, Table, Image, Tabs, Tooltip, Space, Button, message, Row, Col } from 'antd';
import { CustomerServiceOutlined, CopyOutlined, CheckCircleTwoTone, CloseCircleTwoTone, MinusCircleTwoTone } from '@ant-design/icons';
import './UserDetail.css';

const { TabPane } = Tabs;

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [notificationPage, setNotificationPage] = useState(0);
  const [notificationPageSize, setNotificationPageSize] = useState(10);
  
  const { loading: userLoading, error: userError, data: userData } = useQuery(getUser, {
    variables: { userId: id },
  });
  const { data: creditData } = useQuery(getUserCreditHistory, { 
    variables: { userId: id } 
  });
  const { data: videoData } = useQuery(dashboardUserVideos, { 
    variables: { userId: id } 
  });
  const { data: imageData } = useQuery(dashboardUserImages, {
    variables: { userId: id }
  });
  const { data: notificationData, loading: notificationLoading } = useQuery(getUserNotifications, {
    variables: { 
      userId: id,
      skip: notificationPage * notificationPageSize,
      take: notificationPageSize
    }
  });

  if (userLoading) return <Spin size="large" className="user-detail-loading" />;
  if (userError) return <Alert type="error" message={`Error: ${userError.message}`} />;

  const user = userData.getUser;
  const creditHistory = creditData?.getUserCreditHistory || [];
  const videos = videoData?.dashboardUserVideos || [];
  const images = imageData?.dashboardUserImages || [];
  const dailyStreakData = user?.dailyStreak || [];
  const notifications = notificationData?.getUserNotifications || [];

  const truncateText = (text, maxLength = 30) => {
    if (!text) return '-';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const getNotificationTypeColor = (type) => {
    const colors = {
      LIKE: 'blue',
      MILESTONE_10: 'green',
      MILESTONE_25: 'green',
      MILESTONE_50: 'gold',
      MILESTONE_100: 'red',
      FEED_CONFIRMED: 'cyan',
      VIDEO_COMPLETED: 'green',
      VIDEO_FAILED: 'red',
      CREDIT_RECEIVED: 'purple',
      DAILY_STREAK: 'orange',
      PROMPT_COPY: 'magenta',
      OTHER: 'default',
    };
    return colors[type] || 'default';
  };

  const notificationColumns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: (type) => (
        <Tag color={getNotificationTypeColor(type)}>
          {type}
        </Tag>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (title) => (
        <Tooltip placement="topLeft" title={title}>
          {title}
        </Tooltip>
      ),
    },
    {
      title: 'Body',
      dataIndex: 'body',
      key: 'body',
      width: 300,
      ellipsis: {
        showTitle: false,
      },
      render: (body) => (
        <Tooltip placement="topLeft" title={body}>
          {body}
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isRead',
      key: 'isRead',
      width: 100,
      render: (isRead) => (
        <Tag color={isRead ? 'default' : 'blue'}>
          {isRead ? 'Read' : 'Unread'}
        </Tag>
      ),
    },
    {
      title: 'Sent At',
      dataIndex: 'sentAt',
      key: 'sentAt',
      width: 180,
      render: (date) => new Date(date).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    },
    {
      title: 'Read At',
      dataIndex: 'readAt',
      key: 'readAt',
      width: 180,
      render: (date) => date ? new Date(date).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }) : '-',
    },
  ];

  const dailyStreakColumns = [
    {
      title: 'Day Number',
      dataIndex: 'dayNumber',
      key: 'dayNumber',
      width: 120,
      render: (dayNumber) => (
        <span style={{ fontWeight: 600, color: '#1890ff' }}>Day {dayNumber}</span>
      )
    },
    {
      title: 'Credits Awarded',
      dataIndex: 'creditsAwarded',
      key: 'creditsAwarded',
      width: 150,
      render: (credits) => (
        <span style={{ fontWeight: 600, color: '#52c41a' }}>+{credits}</span>
      )
    },
    {
      title: 'Claimed Date',
      dataIndex: 'claimedDate',
      key: 'claimedDate',
      width: 200,
      render: (date) => date ? new Date(date).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }) : '-',
    },
    {
      title: 'Timezone Offset',
      dataIndex: 'claimedTimezoneOffset',
      key: 'claimedTimezoneOffset',
      width: 150,
      render: (offset) => offset || '-'
    },
    {
      title: 'Was Reset',
      dataIndex: 'wasReset',
      key: 'wasReset',
      width: 120,
      render: (wasReset) => (
        <Tag color={wasReset ? 'red' : 'green'}>
          {wasReset ? 'Yes' : 'No'}
        </Tag>
      )
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      render: (date) => new Date(date).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
    },
  ];

  const creditColumns = [
    {
      title: 'Transaction ID',
      dataIndex: 'transactionId',
      key: 'transactionId',
      width: 250,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
    },
    {
      title: 'Credit ID',
      dataIndex: 'creditId',
      key: 'creditId',
      width: 250,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      render: (date) => new Date(date).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
    },
  ];

  const videoColumns = [
    {
      title: 'Prompt',
      dataIndex: 'prompt',
      key: 'prompt',
      width: 400,
    },
    {
      title: 'Thumbnail',
      dataIndex: 'thumbnailUrl',
      key: 'thumbnailUrl',
      width: 150,
      render: (url) => (
        <Image 
          src={url} 
          alt="Thumbnail" 
          style={{ width: '100px', height: '100px', objectFit: 'cover' }}
          preview={{
            maskClassName: 'customize-mask',
            mask: <div>View</div>
          }}
        />
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date) => new Date(date).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
      render: (date) => new Date(date).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
    }
  ];

  const imageColumns = [
    {
      title: 'Prompt',
      dataIndex: 'prompt',
      key: 'prompt',
      width: 400,
    },
    {
      title: 'Image',
      dataIndex: 'url',
      key: 'url',
      width: 150,
      render: (url) => url ? (
        <Image
          src={url}
          alt="Generated"
          style={{ width: '100px', height: '100px', objectFit: 'cover' }}
          preview={{
            maskClassName: 'customize-mask',
            mask: <div>View</div>
          }}
        />
      ) : '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const colors = {
          SUCCEEDED: 'green',
          FAILED: 'red',
          RUNNING: 'blue',
          IN_QUE: 'orange',
          PENDING: 'gold',
          THROTTLED: 'purple',
          MODERATION: 'magenta',
        };
        return <Tag color={colors[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: 'Model',
      dataIndex: 'modelName',
      key: 'modelName',
      width: 150,
      render: (modelName) => modelName || '-',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date) => new Date(date).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
      render: (date) => new Date(date).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
    }
  ];

  const handleSupportClick = () => {
    if (user?.rcUserId) {
      navigate(`/support?rcUserId=${user.rcUserId}`);
    } else {
      message.warning('RevenueCat User ID not available');
    }
  };

  return (
    <div className="user-detail-container">
      <Card
        title={
          <Space>
            <span>User Details</span>
            <Button
              type="primary"
              icon={<CustomerServiceOutlined />}
              onClick={handleSupportClick}
              disabled={!user?.rcUserId}
            >
              Go to Support
            </Button>
          </Space>
        }
      >
        <div className="user-info-section">
          <Row gutter={[24, 24]}>
            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">RevenueCat ID</div>
                <div className="detail-value with-copy">
                  <span className="text-value">{user.rcUserId || '-'}</span>
                  {user.rcUserId && (
                    <Tooltip title="Copy ID">
                      <CopyOutlined
                        className="copy-icon"
                        onClick={() => {
                          navigator.clipboard.writeText(user.rcUserId);
                          message.success('RevenueCat ID copied to clipboard');
                        }}
                      />
                    </Tooltip>
                  )}
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">User ID</div>
                <div className="detail-value with-copy">
                  <span className="text-value">{user.id}</span>
                  <Tooltip title="Copy ID">
                    <CopyOutlined
                      className="copy-icon"
                      onClick={() => {
                        navigator.clipboard.writeText(user.id);
                        message.success('User ID copied to clipboard');
                      }}
                    />
                  </Tooltip>
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">Store</div>
                <div className="detail-value">
                  <span className="text-value">{user.store || '-'}</span>
                </div>
              </div>
            </Col>

            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">
                  FCM ID
                  {user.fcmId && (
                    <Tooltip title="Copy FCM ID">
                      <Tag
                        color="blue"
                        icon={<CopyOutlined />}
                        style={{ 
                          marginLeft: '8px', 
                          cursor: 'pointer',
                          fontSize: '11px'
                        }}
                        onClick={() => {
                          navigator.clipboard.writeText(user.fcmId);
                          message.success('FCM ID copied to clipboard');
                        }}
                      >
                        Copy
                      </Tag>
                    </Tooltip>
                  )}
                </div>
                <div className="detail-value">
                  <Tooltip title={user.fcmId}>
                    <span className="text-value">
                      {user.fcmId ? truncateText(user.fcmId, 20) : '-'}
                    </span>
                  </Tooltip>
                </div>
              </div>
            </Col>

            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">Role</div>
                <div className="detail-value">
                  <Tag color={user.role === 'ADMIN' ? 'green' : 'blue'}>
                    {user.role}
                  </Tag>
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">User Type</div>
                <div className="detail-value">
                  <Tag color={user.isOrganic === null ? 'default' : user.isOrganic ? 'green' : 'blue'}>
                    {user.isOrganic === null ? 'Unknown' : user.isOrganic ? 'Organic' : 'Non-Organic'}
                  </Tag>
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">Premium Status</div>
                <div className="detail-value">
                  <Tag color={user.isPremium ? 'gold' : 'default'}>
                    {user.isPremium ? 'Premium' : 'Regular'}
                  </Tag>
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">Consents</div>
                <div className="detail-value">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {user.aiProcessingConsent === null || user.aiProcessingConsent === undefined ? (
                      <MinusCircleTwoTone twoToneColor="#bfbfbf" />
                    ) : user.aiProcessingConsent ? (
                      <CheckCircleTwoTone twoToneColor="#52c41a" />
                    ) : (
                      <CloseCircleTwoTone twoToneColor="#ff4d4f" />
                    )}
                    <span>AI Processing Consent</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {user.photoUploadConsent === null || user.photoUploadConsent === undefined ? (
                      <MinusCircleTwoTone twoToneColor="#bfbfbf" />
                    ) : user.photoUploadConsent ? (
                      <CheckCircleTwoTone twoToneColor="#52c41a" />
                    ) : (
                      <CloseCircleTwoTone twoToneColor="#ff4d4f" />
                    )}
                    <span>Photo Upload Consent</span>
                  </div>
                </div>
              </div>
            </Col>

            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">Premium Expiry</div>
                <div className="detail-value">
                  <span className="text-value">
                    {user.premiumExpiryDate ? new Date(user.premiumExpiryDate).toLocaleString('tr-TR', {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : '-'}
                  </span>
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">Credits</div>
                <div className="detail-value">
                  <div className="stats-row">
                    <span className="stat-item">
                      <span className="stat-label">Total:</span>
                      <span className="stat-number">{user.totalCredit?.total || 0}</span>
                    </span>
                    <span className="stat-item">
                      <span className="stat-label">Used:</span>
                      <span className="stat-number">{user.usedCreditCount || 0}</span>
                    </span>
                  </div>
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">Moderations</div>
                <div className="detail-value">
                  <span className="text-value">{user.moderations?.total || 0}</span>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <Tabs defaultActiveKey="1" className="detail-tabs">
          <TabPane tab="Credit History" key="1">
            <Table
              columns={creditColumns}
              dataSource={creditHistory}
              rowKey="id"
              pagination={{
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} records`,
                pageSizeOptions: ['5', '10', '20', '50'],
                defaultPageSize: 5
              }}
              scroll={{ x: 'max-content' }}
            />
          </TabPane>
          
          <TabPane tab="Videos" key="2">
            <Table
              columns={withRowLink(videoColumns, (record) => `/video/${record.id}`, { exclude: ['thumbnailUrl'] })}
              dataSource={videos}
              rowKey="id"
              pagination={{
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} records`,
                pageSizeOptions: ['5', '10', '20', '50'],
                defaultPageSize: 5
              }}
              scroll={{ x: 'max-content' }}
              onRow={(record) => ({
                ...getRowLinkProps(`/video/${record.id}`, () => navigate(`/video/${record.id}`)),
              })}
            />
          </TabPane>

          <TabPane tab="Images" key="3">
            <Table
              columns={withRowLink(imageColumns, (record) => `/image/${record.id}`, { exclude: ['url'] })}
              dataSource={images}
              rowKey="id"
              pagination={{
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} records`,
                pageSizeOptions: ['5', '10', '20', '50'],
                defaultPageSize: 5
              }}
              scroll={{ x: 'max-content' }}
              onRow={(record) => ({
                ...getRowLinkProps(`/image/${record.id}`, () => navigate(`/image/${record.id}`)),
              })}
            />
          </TabPane>

          <TabPane tab="Notifications" key="4">
            <Table
              columns={notificationColumns}
              dataSource={notifications}
              rowKey="id"
              loading={notificationLoading}
              pagination={{
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} records`,
                pageSizeOptions: ['5', '10', '20', '50'],
                pageSize: notificationPageSize,
                current: notificationPage + 1,
                onChange: (page, pageSize) => {
                  setNotificationPage(page - 1);
                  setNotificationPageSize(pageSize);
                },
              }}
              scroll={{ x: 'max-content' }}
              rowClassName={(record) => !record.isRead ? 'unread-notification-row' : ''}
            />
          </TabPane>

          {dailyStreakData && dailyStreakData.length > 0 && (
            <TabPane tab="Daily Streak" key="5">
              <Table
                columns={dailyStreakColumns}
                dataSource={dailyStreakData}
                rowKey="id"
                pagination={{
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} records`,
                  pageSizeOptions: ['5', '10', '20', '50'],
                  defaultPageSize: 5
                }}
                scroll={{ x: 'max-content' }}
              />
            </TabPane>
          )}

          {user.moderations?.total > 0 && (
            <TabPane tab="Blacklist" key="6">
              <Table
                columns={[
                  { 
                    title: 'ID', 
                    dataIndex: 'id', 
                    key: 'id',
                    width: '10%'
                  },
                  { 
                    title: 'Prompt', 
                    dataIndex: 'prompt', 
                    key: 'prompt',
                    width: '25%',
                    render: (text) => (
                      <Tooltip title={text} placement="topLeft">
                        <span className="truncated-text">{truncateText(text)}</span>
                      </Tooltip>
                    )
                  },
                  { 
                    title: 'Input Image', 
                    dataIndex: 'inputImg', 
                    key: 'inputImg',
                    width: '15%',
                    render: (url) => url ? (
                      <Image
                        src={url}
                        alt="Input"
                        style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                        preview={{
                          maskClassName: 'customize-mask',
                          mask: <div>View</div>
                        }}
                      />
                    ) : '-'
                  },
                  { 
                    title: 'Type', 
                    dataIndex: 'type', 
                    key: 'type',
                    width: '15%'
                  },
                  { 
                    title: 'Category', 
                    dataIndex: 'category', 
                    key: 'category',
                    width: '15%'
                  },
                  { 
                    title: 'Created At', 
                    dataIndex: 'createdAt', 
                    key: 'createdAt',
                    width: '15%',
                    render: (date) => new Date(date).toLocaleString('tr-TR', {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  },
                ]}
                dataSource={user.moderations.blackLists}
                rowKey="id"
                pagination={{
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} records`,
                  pageSizeOptions: ['5', '10', '20', '50'],
                  defaultPageSize: 5
                }}
              />
            </TabPane>
          )}
        </Tabs>
      </Card>
    </div>
  );
};

export default UserDetail; 